import { useState, useEffect } from 'react'
import api from '../services/api'

interface TipoJornada {
  codigo: number
  descricao: string
  forma_jornada: number
  jornada_especifica: number | null
  mostra_jornada_separada: number
  contabiliza_banco: number
  forma_visualizar: number
}

const FORMA_JORNADA = [
  { valor: 0, label: 'Carrega jornada cadastro colaborador' },
  { valor: 1, label: 'Carrega jornada específica' },
]

const FORMA_VISUALIZAR = [
  { valor: 0, label: 'Pela hora lançada' },
  { valor: 1, label: 'Pela jornada específica' },
  { valor: 2, label: 'Quantidade de dias' },
]

function numeroParaHM(v: number | null | undefined): string {
  if (v === null || v === undefined) return ''
  const horas = Math.floor(v)
  const minutos = Math.round((v - horas) * 100)
  return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`
}

function hmParaNumero(hm: string): number {
  if (!hm.trim()) return 0
  const [h, m] = hm.split(':')
  return Number(h || 0) + Number(m || 0) / 100
}

function vazio(proximoCodigo: number): TipoJornada {
  return {
    codigo: proximoCodigo,
    descricao: '',
    forma_jornada: 0,
    jornada_especifica: 0,
    mostra_jornada_separada: 0,
    contabiliza_banco: 1,
    forma_visualizar: 0,
  }
}

function Lista({ lista, onAdicionar, onEditar, onExcluir }: {
  lista: TipoJornada[]
  onAdicionar: () => void
  onEditar: (t: TipoJornada) => void
  onExcluir: (t: TipoJornada) => void
}) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>
          Tipos cadastrados ({lista.length})
        </h3>
        <button className="btn btn-primary" onClick={onAdicionar}>+ Adicionar</button>
      </div>

      {lista.length === 0 && (
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhum tipo de jornada cadastrado.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {lista.map(t => (
          <div
            key={t.codigo}
            style={{
              border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t.codigo} - {t.descricao}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>{FORMA_JORNADA.find(o => o.valor === t.forma_jornada)?.label}</span>
                {t.forma_jornada === 1 && <span>Jornada: <b>{numeroParaHM(t.jornada_especifica)}</b></span>}
                <span>Banco de horas: <b>{t.contabiliza_banco === 0 ? 'Sim' : 'Não'}</b></span>
                <span>Visualização: <b>{FORMA_VISUALIZAR.find(o => o.valor === t.forma_visualizar)?.label}</b></span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => onEditar(t)}>Editar</button>
              <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => onExcluir(t)}>Excluir</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Formulario({ editando, formInicial, onVoltar, onSalvo }: {
  editando: boolean
  formInicial: TipoJornada
  onVoltar: () => void
  onSalvo: () => void
}) {
  const [form, setForm] = useState<TipoJornada>(formInicial)
  const [jornadaHM, setJornadaHM] = useState(numeroParaHM(formInicial.jornada_especifica) || '00:00')
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function salvar() {
    if (!form.descricao.trim()) {
      setErro('Descrição é obrigatória.')
      return
    }
    setSalvando(true)
    setErro('')
    const body = { ...form, jornada_especifica: hmParaNumero(jornadaHM) }
    try {
      if (editando) {
        await api.put(`/cartao-ponto/tipos-jornada/${form.codigo}`, body)
      } else {
        await api.post('/cartao-ponto/tipos-jornada', body)
      }
      onSalvo()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>
          {editando ? 'Editar Tipo de Jornada' : 'Novo Tipo de Jornada'}
        </h3>
        <button className="btn btn-outline" onClick={onVoltar}>Voltar</button>
      </div>

      <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Código *</label>
          <input
            type="number"
            value={form.codigo}
            disabled={editando}
            onChange={e => setForm(f => ({ ...f, codigo: Number(e.target.value) }))}
          />
        </div>
        <div className="field">
          <label>Descrição * (máx. 10 caracteres)</label>
          <input
            type="text"
            value={form.descricao}
            maxLength={10}
            onChange={e => setForm(f => ({ ...f, descricao: e.target.value.toUpperCase().slice(0, 10) }))}
            placeholder="Ex: FOLGA"
          />
        </div>

        <div className="field">
          <label>Forma de jornada</label>
          <select
            value={form.forma_jornada}
            onChange={e => setForm(f => ({ ...f, forma_jornada: Number(e.target.value) }))}
          >
            {FORMA_JORNADA.map(o => <option key={o.valor} value={o.valor}>{o.label}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Jornada específica (HH:MM)</label>
          <input
            type="text"
            value={jornadaHM}
            disabled={form.forma_jornada !== 1}
            onChange={e => setJornadaHM(e.target.value)}
            placeholder="08:00"
          />
        </div>

        <div className="field">
          <label>Mostra jornada separada na impressão da ficha</label>
          <select
            value={form.mostra_jornada_separada}
            onChange={e => setForm(f => ({ ...f, mostra_jornada_separada: Number(e.target.value) }))}
          >
            <option value={0}>Não</option>
            <option value={1}>Sim</option>
          </select>
        </div>
        <div className="field">
          <label>
            Contabiliza no banco de horas{' '}
            <span style={{ color: '#9ca3af', fontWeight: 400 }} title="Neste campo o valor 0 significa Sim e 1 significa Não, conforme a convenção da tabela CARTTD">(?)</span>
          </label>
          <select
            value={form.contabiliza_banco}
            onChange={e => setForm(f => ({ ...f, contabiliza_banco: Number(e.target.value) }))}
          >
            <option value={0}>Sim</option>
            <option value={1}>Não</option>
          </select>
        </div>

        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Forma de visualizar</label>
          <select
            value={form.forma_visualizar}
            onChange={e => setForm(f => ({ ...f, forma_visualizar: Number(e.target.value) }))}
          >
            {FORMA_VISUALIZAR.map(o => <option key={o.valor} value={o.valor}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.5rem' }}>{erro}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Adicionar'}
        </button>
        <button className="btn btn-outline" onClick={onVoltar}>Cancelar</button>
      </div>
    </div>
  )
}

export default function TiposJornada() {
  const [lista, setLista] = useState<TipoJornada[]>([])
  const [carregado, setCarregado] = useState(false)
  const [modo, setModo] = useState<'lista' | 'form'>('lista')
  const [formAtual, setFormAtual] = useState<TipoJornada | null>(null)
  const [editando, setEditando] = useState(false)

  async function carregar() {
    const r = await api.get('/cartao-ponto/tipos-jornada')
    setLista(r.data)
    setCarregado(true)
    return r.data as TipoJornada[]
  }

  useEffect(() => { carregar() }, [])

  function adicionar() {
    const proximo = lista.length ? Math.max(...lista.map(t => t.codigo)) + 1 : 1
    setFormAtual(vazio(proximo))
    setEditando(false)
    setModo('form')
  }

  function editar(t: TipoJornada) {
    setFormAtual({ ...t })
    setEditando(true)
    setModo('form')
  }

  async function excluir(t: TipoJornada) {
    if (!window.confirm(`Excluir o tipo de jornada "${t.codigo} - ${t.descricao}"? Isso pode afetar lançamentos que já usam esse tipo.`)) return
    await api.delete(`/cartao-ponto/tipos-jornada/${t.codigo}`)
    await carregar()
  }

  function voltar() {
    setModo('lista')
    setFormAtual(null)
  }

  async function salvo() {
    await carregar()
    setModo('lista')
    setFormAtual(null)
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Tipos de Jornada
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="Tabela frota.carttd">CARTTD</span>
      </h2>

      {!carregado ? null : modo === 'lista' ? (
        <Lista lista={lista} onAdicionar={adicionar} onEditar={editar} onExcluir={excluir} />
      ) : formAtual ? (
        <Formulario
          editando={editando}
          formInicial={formAtual}
          onVoltar={voltar}
          onSalvo={salvo}
        />
      ) : null}
    </div>
  )
}
